import { Alert, Box, SelectChangeEvent } from '@mui/material';
import { CompanyRole } from 'constants/CompanyRole';
import { profileSelector } from 'store/selectors/profile-selectors';
import UPButton from 'components/button';
import UPInput from 'components/input';
import UPMultipleSelectWithCheckbox from 'components/multiple-select-with-checkbox/UPMultipleSelectWithCheckbox';
import { EmployerLabel } from 'constants/EmployerLabel';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'store';
import { getParitairComiteSelectItemsAction } from 'store/actions/paritair-comite-actions';
import { getMotiveExceptionsAction } from 'store/actions/motive-exception-actions';
import { motiveExceptionsSelector } from 'store/selectors/motive-exception-selectors';
import {
  addPayrollAction,
  addPayrollErrorAction,
  addPayrollSuccessAction,
  getPayrollAction,
  updatePayrollAction,
  updatePayrollErrorAction,
  updatePayrollSuccessAction,
} from 'store/actions/payroll-actions';
import { paritairComiteSelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import {
  addPayrollErrorSelector,
  addPayrollSuccessSelector,
  payrollSelector,
  updatePayrollErrorSelector,
  updatePayrollSuccessSelector,
} from 'store/selectors/payroll-selectors';
import { getUpDropdownItemsFromEnum } from 'utils/helpers';
import ICoefficientPerWorkerclass from 'types/company/payroll/ICoefficientPerWorkerclass';
import IContractMotive from 'types/contract/IContractMotive';
import UPDropdownWithInput from 'components/dropdown-with-input';
import ListboxWithCheckboxes from 'components/listbox-with-checkbox/ListBoxWithCheckbox';
import { CoefficientsPerWorkerclassGrid } from '.';
import MotiveExceptions from './motive-exceptions/MotiveExceptions';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { UPDropdown, UPDropdownItem } from '../../../components/dropdown/index';
import {
  COMPANY_INFORMATION__COMPANY_UPDATED_SUCCESSFULLY,
  COMPANY_PAYROLL__AFTERNOON_INTERVAL,
  COMPANY_PAYROLL__DEFAULT_BREAKTIME,
  COMPANY_PAYROLL__DEFAULT_CONTRACT_MOTIVE,
  COMPANY_PAYROLL__EMPLOYER_LABEL,
  COMPANY_PAYROLL__EVENING_INTERVAL,
  COMPANY_PAYROLL__INVALID_DEFAULT_BREAKTIME_FORMAT,
  COMPANY_PAYROLL__MORNING_INTERVAL,
  COMPANY_PAYROLL__PARITAIR_COMITE,
  COMPANY_PAYROLL__POSSIBLE_CONTRACT_MOTIVES,
  COMPANY_PAYROLL__WORKHOURS_SETTINGS,
  CONTRACT_MOTIVE__STILL_USED_BY_EXCEPTION,
  GENERAL__CANCEL_BUTTON,
  GENERAL__ERROR_MESSAGE,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
  MOTIVE_EXCEPTION__TAB_TITLE,
  VALIDATION__IS_REQUIRED,
  COMPANY_PAYROLL__INVALID_INTERVAL_FORMAT,
} from '../../../translations/constants';
import ICompanyPayrollGeneral from '../../../types/company/payroll/ICompanyPayrollGeneral';
import { areAvailabilityIntervalsValid, isDefaultBreakTimeValid } from '../helpers';
import { CloseAlertIcon } from '../styles';
import { PayrollContainer, PayrollInputLabel, PayrollTabAlerts, PayrollTabContainer } from './styles';
import BREAK_INTERVAL_HOUR_OPTIONS from './PayrollDefaultBreakIntervalOptions';

const PayrollGeneralInitialState: ICompanyPayrollGeneral = {
  paritairComites: [],
  coefficientsPerWorkerclass: [],
  contractMotives: [],
  possibleMotives: [],
  defaultContractMotive: '',
  id: 0,
  companyId: 0,
  employerLabel: '',
  morningInterval: '',
  afternoonInterval: '',
  eveningInterval: '',
  defaultBreakTime: '',
};

const PayrollGeneral = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const paritairComiteOptions = useAppSelector(paritairComiteSelectItemsSelector);
  const isAdmin = useAppSelector(profileSelector).role === CompanyRole.ADMIN;
  const payroll = useAppSelector(payrollSelector);
  const motiveExceptions = useAppSelector(motiveExceptionsSelector);
  const addPayrollError = useAppSelector(addPayrollErrorSelector);
  const updatePayrollError = useAppSelector(updatePayrollErrorSelector);
  const addPayrollSuccess = useAppSelector(addPayrollSuccessSelector);
  const updatePayrollSuccess = useAppSelector(updatePayrollSuccessSelector);

  const [coefficientsPerWorkerclass, setCoefficientsPerWorkerclass] = useState<ICoefficientPerWorkerclass[]>([]);
  const [contractMotives, setContractMotives] = useState<IContractMotive[]>([]);
  const [motiveInUseError, setMotiveInUseError] = useState('');

  const contractMotivesItems = useMemo(() => {
    return contractMotives.map(cm => ({ value: cm.code.toString(), label: t(cm.nameConstant) }));
  }, [contractMotives, t]);

  const possibleContractMotivesItems = useMemo(() => {
    return contractMotives
      .filter(cm => cm.isPossible)
      .map(cm => ({ value: cm.code.toString(), label: t(cm.nameConstant) }));
  }, [contractMotives, t]);

  const employerLabelOptions: UPDropdownItem[] = useMemo(() => {
    return getUpDropdownItemsFromEnum(EmployerLabel);
  }, []);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<ICompanyPayrollGeneral>({
    defaultValues: PayrollGeneralInitialState,
  });

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsAction());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPayrollAction(toNumber(companyId)));
    dispatch(getMotiveExceptionsAction(toNumber(companyId)));
  }, [dispatch, companyId]);

  useEffect(() => {
    if (payroll) {
      setContractMotives(payroll.contractMotives);
    }
    if (payroll && payroll.id !== 0) {
      reset(payroll);
      setCoefficientsPerWorkerclass(payroll.coefficientsPerWorkerclass);
    }
  }, [payroll, reset]);

  const onPCChange = useCallback(
    event => {
      const {
        target: { value },
      } = event;
      setValue('paritairComites', typeof value === 'string' ? value.split(',') : value);

      clearErrors();
    },
    [setValue, clearErrors],
  );

  useEffect(() => {
    return () => {
      dispatch(addPayrollSuccessAction(false));
      dispatch(updatePayrollSuccessAction(false));
      dispatch(addPayrollErrorAction(''));
      dispatch(updatePayrollErrorAction(''));
      setMotiveInUseError('');
    };
  }, [dispatch]);

  useEffect(() => {
    if (addPayrollSuccess || updatePayrollSuccess) {
      dispatch(getPayrollAction(toNumber(companyId)));
    }
  }, [addPayrollSuccess, updatePayrollSuccess, dispatch, companyId]);

  const onSavePayroll: SubmitHandler<any> = useCallback(() => {
    const values = getValues();
    const morningIntervalEnds = values.morningInterval.split(' - ');
    const afternoonIntervalEnds = values.afternoonInterval.split(' - ');
    const eveningIntervalEnds = values.eveningInterval.split(' - ');
    const areIntervalsValid = areAvailabilityIntervalsValid(
      morningIntervalEnds,
      afternoonIntervalEnds,
      eveningIntervalEnds,
      setError,
      t,
    );

    const isDefaultBreakValid = isDefaultBreakTimeValid(values.defaultBreakTime, setError, t);

    if (!areIntervalsValid || !isDefaultBreakValid) {
      return;
    }

    setMotiveInUseError('');

    if (!values.possibleMotives.includes(values.defaultContractMotive)) {
      setError('defaultContractMotive', { type: 'required' });
      return;
    }

    const motiveCodesStillInUse = Array.from(
      new Set(
        motiveExceptions
          .map(motiveException => motiveException.contractMotiveId.toString())
          .filter(code => !values.possibleMotives.includes(code)),
      ),
    );
    if (motiveCodesStillInUse.length > 0) {
      setError('possibleMotives', { type: 'motiveInUse' });
      setMotiveInUseError(t(CONTRACT_MOTIVE__STILL_USED_BY_EXCEPTION));
      return;
    }

    if (values.paritairComites.length === 0) {
      setError('paritairComites', { type: 'required' });
      return;
    }
    values.companyId = toNumber(companyId);
    if (values.id === 0) {
      values.coefficientsPerWorkerclass = coefficientsPerWorkerclass.map(c => ({
        ...c,
        id: 0,
      }));
      dispatch(addPayrollAction(values));
    } else {
      values.coefficientsPerWorkerclass = coefficientsPerWorkerclass;
      dispatch(updatePayrollAction(values));
    }
  }, [coefficientsPerWorkerclass, companyId, dispatch, getValues, motiveExceptions, setError, t]);

  const onCancel = useCallback(() => {
    reset(payroll);
    navigate(`/company/${companyId}/general`);
  }, [companyId, navigate, payroll, reset]);

  const filterByHour = (options: UPDropdownItem[], state: { inputValue: string }) => {
    return options.filter(option => option.label.toLowerCase().startsWith(state.inputValue.toLowerCase()));
  };

  const onContractMotiveSelect = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setContractMotives(
      contractMotives.map(cm => ({
        ...cm,
        isPossible:
          typeof value === 'string'
            ? value.split(',').includes(cm.code.toString())
            : value.includes(cm.code.toString()),
      })),
    );
  };

  const castToArray = (value: unknown): any[] => {
    return Array.isArray(value) ? value : [];
  };

  return (
    <PayrollTabContainer>
      <PayrollContainer>
        <Box sx={{ marginBottom: '32px' }}>
          <Controller
            name="paritairComites"
            control={control}
            rules={{
              validate: v => (Array.isArray(v) && v.length > 0) || 'Select at least one joint commission',
              required: { value: true, message: t(VALIDATION__IS_REQUIRED) },
            }}
            render={({ field }) => (
              <ListboxWithCheckboxes
                label={t(COMPANY_PAYROLL__PARITAIR_COMITE)}
                options={paritairComiteOptions}
                value={field.value ?? []}
                onChange={field.onChange}
                required
                searchable
                enableBulkActions
                maxListHeight={180}
                helperText={t(VALIDATION__IS_REQUIRED)}
              />
            )}
          />
        </Box>
        <Box sx={{ marginBottom: '32px' }}>
          <CoefficientsPerWorkerclassGrid
            coefficientsPerWorkerclass={coefficientsPerWorkerclass}
            setCoefficientsPerWorkerclass={setCoefficientsPerWorkerclass}
          />
        </Box>

        <Box sx={{ marginBottom: '32px' }}>
          <Controller
            name="employerLabel"
            control={control}
            render={({ field }) => (
              <UPDropdown
                items={employerLabelOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder={t(GENERAL__SELECT)}
                label={t(COMPANY_PAYROLL__EMPLOYER_LABEL)}
              />
            )}
          />
        </Box>

        <Box sx={{ marginTop: '32px', marginBottom: '32px' }}>
          <PayrollInputLabel>{t(MOTIVE_EXCEPTION__TAB_TITLE)}</PayrollInputLabel>
          <Box sx={{ marginTop: '16px' }}>
            <Controller
              name="possibleMotives"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <UPMultipleSelectWithCheckbox
                  items={contractMotivesItems}
                  selectedItems={field.value}
                  renderValue={selected => {
                    const v = castToArray(selected);
                    return contractMotivesItems
                      .filter(o => v.includes(o.value))
                      .map(o => o.label)
                      .join(', ');
                  }}
                  onChange={event => {
                    field.onChange(event.target.value);
                    onContractMotiveSelect(event);
                  }}
                  placeholder={t(GENERAL__SELECT)}
                  label={t(COMPANY_PAYROLL__POSSIBLE_CONTRACT_MOTIVES)}
                  required
                  error={!!errors.possibleMotives}
                  helperText={errors.possibleMotives && (motiveInUseError || t(VALIDATION__IS_REQUIRED))}
                />
              )}
            />
          </Box>

          <Box sx={{ marginTop: '16px' }}>
            <Controller
              name="defaultContractMotive"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <UPDropdown
                  items={possibleContractMotivesItems}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t(GENERAL__SELECT)}
                  label={t(COMPANY_PAYROLL__DEFAULT_CONTRACT_MOTIVE)}
                  error={!!errors.defaultContractMotive}
                  helperText={errors.defaultContractMotive && t(VALIDATION__IS_REQUIRED)}
                  required
                />
              )}
            />
          </Box>
          {isAdmin && (
            <Box sx={{ marginTop: '16px' }}>
              <MotiveExceptions />
            </Box>
          )}
        </Box>
        <Box>
          <Box sx={{ marginTop: '32px' }}>
            <PayrollInputLabel>{t(COMPANY_PAYROLL__WORKHOURS_SETTINGS)}</PayrollInputLabel>
            <Box sx={{ marginTop: '16px' }}>
              <Box sx={{ marginTop: '16px' }}>
                <Controller
                  name="defaultBreakTime"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^(0[0-6]:[0-5][0-9])?$/,
                      message: t(COMPANY_PAYROLL__INVALID_DEFAULT_BREAKTIME_FORMAT),
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <UPDropdownWithInput
                      value={value}
                      handleChange={onChange}
                      filterOptions={filterByHour}
                      placeholder=""
                      items={BREAK_INTERVAL_HOUR_OPTIONS}
                      label={t(COMPANY_PAYROLL__DEFAULT_BREAKTIME)}
                      type="defaultBreakTime"
                      error={!!errors.defaultBreakTime}
                      helperText={errors.defaultBreakTime?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ marginTop: '16px' }}>
                <Controller
                  name="morningInterval"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^([01]\d|2[0-3]):([0-5]\d) - ([01]\d|2[0-3]):([0-5]\d)$/,
                      message: t(COMPANY_PAYROLL__INVALID_INTERVAL_FORMAT),
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <UPInput
                      value={value || ''}
                      onChange={onChange}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(COMPANY_PAYROLL__MORNING_INTERVAL)}
                      error={!!errors.morningInterval}
                      helperText={errors.morningInterval?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ marginTop: '16px' }}>
                <Controller
                  name="afternoonInterval"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^([01]\d|2[0-3]):([0-5]\d) - ([01]\d|2[0-3]):([0-5]\d)$/,
                      message: t(COMPANY_PAYROLL__INVALID_INTERVAL_FORMAT),
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <UPInput
                      value={value || ''}
                      onChange={onChange}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(COMPANY_PAYROLL__AFTERNOON_INTERVAL)}
                      error={!!errors.afternoonInterval}
                      helperText={errors.afternoonInterval?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ marginTop: '16px' }}>
                <Controller
                  name="eveningInterval"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^([01]\d|2[0-3]):([0-5]\d) - ([01]\d|2[0-3]):([0-5]\d)$/,
                      message: t(COMPANY_PAYROLL__INVALID_INTERVAL_FORMAT),
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <UPInput
                      value={value || ''}
                      onChange={onChange}
                      placeholder={t(GENERAL__SELECT)}
                      label={t(COMPANY_PAYROLL__EVENING_INTERVAL)}
                      error={!!errors.eveningInterval}
                      helperText={errors.eveningInterval?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ marginTop: '32px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                <Box sx={{ marginRight: '16px' }}>
                  <UPButton text={t(GENERAL__SAVE_BUTTON)} onClick={() => handleSubmit(onSavePayroll)()} />
                </Box>
                <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onCancel} outlined />
              </Box>
            </Box>
          </Box>
        </Box>
      </PayrollContainer>
      <PayrollTabAlerts>
        {(addPayrollSuccess || updatePayrollSuccess) && (
          <Alert
            severity="success"
            icon={false}
            variant="filled"
            sx={{ width: '800px' }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  dispatch(addPayrollSuccessAction(false));
                  dispatch(updatePayrollSuccessAction(false));
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(COMPANY_INFORMATION__COMPANY_UPDATED_SUCCESSFULLY)}
          </Alert>
        )}
        {(addPayrollError || updatePayrollError) && (
          <Alert
            severity="error"
            icon={false}
            variant="filled"
            sx={{ width: '800px' }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  dispatch(addPayrollErrorAction(''));
                  dispatch(updatePayrollErrorAction(''));
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(addPayrollError || updatePayrollError || GENERAL__ERROR_MESSAGE)}
          </Alert>
        )}
      </PayrollTabAlerts>
    </PayrollTabContainer>
  );
};

export default PayrollGeneral;
